import apiRoutes from "@/src/apiRoutes";
import Screen from "@/src/components/Screen";
import { textVariants } from "@/src/components/Text/Text";
import { Avatar, AvatarFallback } from "@/src/components/shad/avatar";
import { Card, CardContent } from "@/src/components/shad/card";
import api from "@/src/utils";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import check from "@assets/checkmark.svg";
import { TraderInfoStats } from "../types";
import { cn } from "@/lib/utils";
import { Suspense, lazy, useState } from "react";
import { first } from "lodash";
import GoBack from "@/src/components/goBack";
import { XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

const Ads = lazy(() => import("./TraderAds"));
const Reviews = lazy(() => import("./TraderReviews"));

const TraderInfo = () => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.traderInfo.${key}`);
  const { username } = useParams();
  const [state, setState] = useState<"ads" | "reviews">("ads");

  const getTraderInfo = async () => {
    const res = await api.post(`${apiRoutes.tradeInfo}${username}/`, {
      type: "info",
    });
    return res.data;
  };

  const { data, isLoading } = useQuery<TraderInfoStats>({
    queryKey: ["get_trader_info"],
    queryFn: getTraderInfo,
  });
  const getTraderVerification = async () => {
    const res = await api.post(`${apiRoutes.tradeInfo}${username}/`, {
      type: "basic",
    });
    return res.data;
  };

  const { data: VeriData } = useQuery({
    queryKey: ["get_trader_veri"],
    queryFn: getTraderVerification,
  });

  const Components = {
    ads: Ads,
    reviews: Reviews,
  };

  const LazyComponents = Components[state];

  return (
    <Screen loading={isLoading}>
      <GoBack />
      <div className="grid gap-5">
        <Card className="py-3">
          <CardContent>
            <div>
              <div className="flex items-center">
                <Avatar className="p-1">
                  <AvatarFallback className="bg-[#2F60E0] p-1 text-white">
                    {first(username)}
                  </AvatarFallback>
                </Avatar>
                <span
                  className={textVariants({ size: "lg", font: "semibold" })}
                >
                  {username}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "p-1 rounded-full ",
                    VeriData?.info?.online_status?.online
                      ? "bg-green-500"
                      : "bg-gray-400"
                  )}
                ></span>
                <span className={textVariants({ size: "xs", variant: "desc" })}>
                  {VeriData?.info?.online_status?.last_seen}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className={textVariants({ size: "sm", variant: "desc" })}>
                  {trans("registered")}: {VeriData?.info?.joined_date}
                </span>
                <div className="flex gap-3">
                  <div className="flex items-center gap-1">
                    <img src={check} alt="+" className="h-4 w-4" />
                    <span className={textVariants()}>
                      {trans("kycVerified")}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {VeriData?.info.phone ? (
                      <img src={check} alt="+" className="h-4 w-4" />
                    ) : (
                      <span className="p-0.5 bg-red-100 rounded-full">
                        <XIcon className="h-3 w-3 text-red-500" />
                      </span>
                    )}
                    <span className={textVariants()}>{trans("phone")}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <img src={check} alt="+" className="h-4 w-4" />
                    <span className={textVariants()}>{trans("email")}</span>
                  </div>
                </div>
              </div>
              <div className="mt-5 grid  gap-4">
                <div className="grid md:grid-cols-3 md:gap-20 gap-5">
                  <div className="flex justify-between">
                    <span className={textVariants({ variant: "desc" })}>
                      {trans("30dCompletedOrders")}
                    </span>
                    <span className={textVariants({ font: "medium" })}>
                      {
                        data?.info?.completed_orders_30
                          ?.completed_trade_orders_count
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={textVariants({ variant: "desc" })}>
                      {trans("avgPaymentTime")}
                    </span>
                    <span className={textVariants({ font: "medium" })}>
                      {data?.info?.release_times?.avg_payment_time}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={textVariants({ variant: "desc" })}>
                      {trans("totalCompleted")}
                    </span>
                    <span className={textVariants({ font: "medium" })}>
                      {
                        data?.info?.completed_orders
                          ?.completed_trade_orders_count
                      }
                    </span>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 md:gap-20 gap-5">
                  <div className="flex justify-between">
                    <span className={textVariants({ variant: "desc" })}>
                      {trans("30dCompletionRate")}
                    </span>
                    <span className={textVariants({ font: "medium" })}>
                      {
                        data?.info?.completed_orders_30
                          ?.percentage_completed_orders
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={textVariants({ variant: "desc" })}>
                      {trans("positiveFeedback")}
                    </span>
                    <span className={textVariants({ font: "medium" })}>
                      {data?.info?.feedbacks?.positive_feedback}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={textVariants({ variant: "desc" })}>
                      {trans("totalCompletionRate")}
                    </span>
                    <span className={textVariants({ font: "medium" })}>
                      {
                        data?.info?.completed_orders
                          ?.percentage_completed_orders
                      }
                    </span>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 md:gap-20 gap-5">
                  <div className="flex justify-between">
                    <span className={textVariants({ variant: "desc" })}>
                      {trans("avgReleaseTime")}
                    </span>
                    <span className={textVariants({ font: "medium" })}>
                      {data?.info?.release_times?.avg_release_time}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={textVariants({ variant: "desc" })}>
                      {trans("negativeFeedback")}
                    </span>
                    <span className={textVariants({ font: "medium" })}>
                      {data?.info?.feedbacks?.negative_feedback}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="py-3">
          <CardContent>
            <div className="flex items-center space-x-6">
              <div
                onClick={() => setState("ads")}
                className={cn(
                  textVariants({ variant: "desc" }),
                  state === "ads" && "font-semibold text-black dark:text-white",
                  "cursor-pointer"
                )}
              >
                {trans("tabs.ads")}
              </div>
              <div
                onClick={() => setState("reviews")}
                className={cn(
                  textVariants({ variant: "desc" }),
                  state === "reviews" &&
                    "font-semibold text-black dark:text-white",
                  "cursor-pointer"
                )}
              >
                {trans("tabs.reviews")}
              </div>
            </div>
            <Suspense>
              <LazyComponents />
            </Suspense>
          </CardContent>
        </Card>
      </div>
    </Screen>
  );
};

export default TraderInfo;
